import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import accountsApi from "../../services/accountsCommonService";
import accountsFlowApi from "../../services/accountsFlowService";
import AccountsFlowConstants from "../../common/AccountsFlowConstants";
import AccountsConstants from "../../common/AccountsConstants";
import { formatINR, ledgerIdOf, todayInput } from "./accountUser";
import LedgerCombo from "./LedgerCombo";
import EntryPreview from "../../Components/Accounts/EntryPreview";
import { ApiAlert } from "./AccountWidgets";
import PageShell from "./PageShell";
import CollapsibleSection from "../../Components/CollapsibleSection/CollapsibleSection";
import ReceiptSplit from "./ReceiptSplit";
import useAccountMeta from "./useAccountMeta";
import "./accounts.css";

const CASH_BANK = ["BANK", "CASH"];

export const ENTRY_CONFIG = {
  RECEIPT: {
    title: "Receipt Voucher",
    label: "Receipt",
    modeLabel: "Receipt Mode",
    debit: { label: "Bank / Cash Ledger (Dr)", groups: CASH_BANK },
    credit: { label: "Party / Income Ledger (Cr)", exclude: CASH_BANK },
  },
  PAYMENT: {
    title: "Payment Voucher",
    label: "Payment",
    modeLabel: "Payment Mode",
    debit: { label: "Party / Expense Ledger (Dr)", exclude: CASH_BANK },
    credit: { label: "Bank / Cash Ledger (Cr)", groups: CASH_BANK },
  },
  PURCHASE: {
    title: "Purchase Voucher",
    label: "Purchase",
    debit: {
      label: "Purchase / Expense Ledger (Dr)",
      groups: ["EXPENSE", "ASSET"],
    },
    credit: { label: "Supplier Ledger (Cr)", groups: ["SUNDRY_CREDITOR"] },
  },
  CREDIT_NOTE: {
    title: "Credit Note",
    label: "Credit note",
    debit: {
      label: "Sales / Discount Ledger (Dr)",
      groups: ["INCOME", "EXPENSE"],
    },
    credit: { label: "Customer Ledger (Cr)", groups: ["SUNDRY_DEBTOR"] },
  },
  DEBIT_NOTE: {
    title: "Debit Note",
    label: "Debit note",
    debit: { label: "Customer Ledger (Dr)", groups: ["SUNDRY_DEBTOR"] },
    credit: { label: "Income Ledger (Cr)", groups: ["INCOME"] },
  },
  JOURNAL: {
    title: "Journal Voucher",
    label: "Journal",
    debit: { label: "Debit Ledger (Dr)", exclude: CASH_BANK },
    credit: { label: "Credit Ledger (Cr)", exclude: CASH_BANK },
  },
};

const amountOk = (text) =>
  /^\d+(\.\d{1,2})?$/.test(String(text).trim()) && Number(text) > 0;

function matches(ledger, side) {
  if (side.groups) return side.groups.includes(ledger.group);
  if (side.exclude) return !side.exclude.includes(ledger.group);
  return true;
}

function mapLineError(fieldErrors) {
  const debit = fieldErrors["lines[0].ledgerId"] || "";
  const credit = fieldErrors["lines[1].ledgerId"] || "";
  const amount =
    fieldErrors["lines[0].debit"] ||
    fieldErrors["lines[1].credit"] ||
    fieldErrors["lines[0]"] ||
    fieldErrors["lines[1]"] ||
    "";
  const preview = fieldErrors.lines || "";
  return { debit, credit, amount, preview };
}

function moneyQuery(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return "";
  return number.toFixed(2);
}

export function settlementLink(voucherType, row) {
  if (row.paymentStatus === "PAID" || row.status === "CANCELLED") return "";
  const open = moneyQuery(
    row.openAmount != null
      ? row.openAmount
      : voucherType === "PURCHASE"
        ? row.totalCredit
        : row.totalDebit,
  );
  if (!open) return "";
  if (voucherType === "SALES") {
    const party = (row.lines || []).find((line) => Number(line.debit) > 0);
    if (!party) return "";
    const params = new URLSearchParams({
      from: "invoice",
      referenceNo: row.voucherNo || "",
      creditLedgerId: party.ledgerId,
      amount: open,
    });
    // return `${AccountsConstants.RECEIPT_CREATE}?${params.toString()}`;
    return `${AccountsFlowConstants.RECEIPT_ENTRY}?${params.toString()}`;
  }
  if (voucherType === "PURCHASE") {
    const party = (row.lines || []).find((line) => Number(line.credit) > 0);
    if (!party) return "";
    const params = new URLSearchParams({
      from: "purchase",
      referenceNo: row.voucherNo || "",
      debitLedgerId: party.ledgerId,
      amount: open,
    });
    return `${AccountsConstants.PAYMENT_CREATE}?${params.toString()}`;
  }
  return "";
}

export default function VoucherEntry({ voucherType }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const linkedFrom = params.get("from") || "";
  const config = ENTRY_CONFIG[voucherType];
  const { meta, error: metaError } = useAccountMeta();
  const [ledgers, setLedgers] = useState([]);
  const [form, setForm] = useState({
    voucherDate: todayInput(),
    referenceNo: params.get("referenceNo") || "",
    paymentMode: "",
    narration: "",
    debitLedgerId: params.get("debitLedgerId") || "",
    creditLedgerId: params.get("creditLedgerId") || "",
    amount: params.get("amount") || "",
    // Cheque details
    chequeNumber: "",
    chequeDate: "",

    // Bank transfer references
    rtgsReferenceNumber: "",
    upiReferenceNumber: "",
    neftReferenceNumber: "",
    impsReferenceNumber: "",

    // Bank details
    customerBankName: "",
  });
  const [saved, setSaved] = useState(null);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [allocations, setAllocations] = useState({});
  const [splitIssue, setSplitIssue] = useState("");
  const [advanceAvailable, setAdvanceAvailable] = useState(0);

  useEffect(() => {
    accountsApi
      .ledgers({ isActive: "true" })
      .then((result) => setLedgers(result?.data || []))
      .catch((err) => setError(err));
  }, [voucherType]);

  const set = (key) => (event) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));
  const debitLedgers = ledgers.filter((row) => matches(row, config.debit));
  const creditLedgers = ledgers.filter((row) => matches(row, config.credit));
  const amount = amountOk(form.amount) ? form.amount.trim() : "0";
  const lines = useMemo(() => {
    const nameOf = (id) =>
      ledgers.find((row) => ledgerIdOf(row) === String(id))?.name || "";
    return [
      {
        ledgerId: form.debitLedgerId,
        ledgerName: nameOf(form.debitLedgerId),
        debit: amount,
        credit: "0",
      },
      {
        ledgerId: form.creditLedgerId,
        ledgerName: nameOf(form.creditLedgerId),
        debit: "0",
        credit: amount,
      },
    ];
  }, [form.debitLedgerId, form.creditLedgerId, amount, ledgers]);

  const sameLedger =
    form.debitLedgerId && form.debitLedgerId === form.creditLedgerId;
  const allocatedPaise = Object.values(allocations).reduce(
    (sum, value) => sum + Math.round((Number(value) || 0) * 100),
    0,
  );
  const isCheque = form.paymentMode === "CHEQUE";
  const isRTGS = form.paymentMode === "RTGS";
  const isUPI = form.paymentMode === "UPI";
  const isNEFT = form.paymentMode === "NEFT";
  const isIMPS = form.paymentMode === "IMPS";

  const clientErrors = {
    voucherDate: !form.voucherDate
      ? "Date is required"
      : form.voucherDate > todayInput()
        ? "Date cannot be in the future"
        : "",
    paymentMode: config.modeLabel && !form.paymentMode ? "Select a mode" : "",
    chequeNumber:
      isCheque && !form.chequeNumber.trim() ? "Cheque number is required" : "",

    chequeDate: isCheque && !form.chequeDate ? "Cheque date is required" : "",

    rtgsReferenceNumber:
      isRTGS && !form.rtgsReferenceNumber.trim()
        ? "RTGS reference number is required"
        : "",

    upiReferenceNumber:
      isUPI && !form.upiReferenceNumber.trim()
        ? "UPI transaction reference number is required"
        : "",

    neftReferenceNumber:
      isNEFT && !form.neftReferenceNumber.trim()
        ? "NEFT reference number is required"
        : "",
    impsReferenceNumber:
      isIMPS && !form.impsReferenceNumber.trim()
        ? "IMPS reference number is required"
        : "",
    debitLedgerId: !form.debitLedgerId
      ? "Select a ledger"
      : sameLedger
        ? "Debit and credit ledgers must be different"
        : "",
    creditLedgerId: !form.creditLedgerId
      ? "Select a ledger"
      : sameLedger
        ? "Debit and credit ledgers must be different"
        : "",
    amount: !String(form.amount).trim()
      ? "Enter an amount"
      : !amountOk(form.amount)
        ? "Enter an amount greater than 0 with at most 2 decimals"
        : "",
    allocations:
      splitIssue ||
      (amountOk(form.amount) &&
      allocatedPaise >
        Math.round(Number(form.amount) * 100) +
          Math.round((Number(advanceAvailable) || 0) * 100)
        ? "Allocated amount is more than the receipt plus the customer's advance"
        : ""),
  };
  const serverFields = mapLineError(error?.fieldErrors || {});
  const formValid =
    Object.values(clientErrors).every((message) => !message) &&
    Number(lines[0].debit) === Number(lines[1].credit);
  const show = (key) =>
    (touched ? clientErrors[key] : "") ||
    (key === "debitLedgerId" ? serverFields.debit : "") ||
    (key === "creditLedgerId" ? serverFields.credit : "") ||
    (key === "amount" ? serverFields.amount : "");

  const clear = () => {
    setForm({
      voucherDate: todayInput(),
      referenceNo: "",
      paymentMode: "",
      narration: "",
      debitLedgerId: "",
      creditLedgerId: "",
      amount: "",

      chequeNumber: "",
      chequeDate: "",
      rtgsReferenceNumber: "",
      upiReferenceNumber: "",
      neftReferenceNumber: "",
      impsReferenceNumber: "",
      customerBankName: "",
    });
    setSaved(null);
    setTouched(false);
    setError(null);
    setAllocations({});
    setSplitIssue("");
    setAdvanceAvailable(0);
  };

  const save = async (event) => {
    event.preventDefault();
    setTouched(true);
    setError(null);
    if (!formValid) return;
    setSaving(true);
    const body = {
      ...(isCheque
        ? {
            chequeNumber: form.chequeNumber.trim(),
            chequeDate: form.chequeDate,
          }
        : {}),

      ...(isRTGS
        ? { rtgsReferenceNumber: form.rtgsReferenceNumber.trim() }
        : {}),

      ...(isUPI ? { upiReferenceNumber: form.upiReferenceNumber.trim() } : {}),

      ...(isNEFT
        ? { neftReferenceNumber: form.neftReferenceNumber.trim() }
        : {}),
      ...(isIMPS
        ? { impsReferenceNumber: form.impsReferenceNumber.trim() }
        : {}),
      voucherType,
      voucherDate: form.voucherDate,
      referenceNo: form.referenceNo.trim(),
      narration: form.narration.trim(),
      lines: lines.map(({ ledgerId, debit, credit }) => ({
        ledgerId,
        debit,
        credit,
      })),
    };
    if (config.modeLabel) body.paymentMode = form.paymentMode;
    const allocationLines = Object.entries(allocations)
      .filter(([, value]) => Number(value) > 0)
      .map(([invoiceNr, value]) => ({ invoiceNr, amount: value }));
    if (voucherType === "RECEIPT" && allocationLines.length)
      body.allocations = allocationLines;
    else if (voucherType === "RECEIPT" && /^INV\d+$/i.test(body.referenceNo))
      body.source = {
        type: "INVOICE",
        invoiceNr: body.referenceNo.toUpperCase(),
      };
    try {
      // Receipts are not posted here: they wait for an approver (accounts flow request RRQ-…).
      const result =
        voucherType === "RECEIPT"
          ? await accountsFlowApi.saveEntry(body)
          : await accountsApi.saveVoucher(body);
      setSaved(result.data);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const locked = Boolean(saved);

  return (
    <PageShell
      title={config.title}
      subtitle={`${config.label} entry · double-entry, ${voucherType === "RECEIPT" ? "sent to admin for approval on save" : "posted on save"}`}
    >
      {saved && saved.requestNo && saved.status !== "APPROVED" && (
        <div className="accounts-note warn">
          <span>
            <strong>{saved.requestNo}</strong> for {formatINR(saved.amount)} is
            waiting for admin approval. It is not in the ledger, outstanding or
            invoice status until it is approved.
          </span>{" "}
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() =>
              navigate(`${AccountsFlowConstants.ENTRY_VIEW}?id=${saved.id}`)
            }
          >
            <i className="bi bi-eye" /> View
          </button>{" "}
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={clear}
          >
            New {config.label.toLowerCase()}
          </button>
        </div>
      )}
      {saved && !(saved.requestNo && saved.status !== "APPROVED") && (
        <div className="accounts-success">
          <span>
            Voucher <strong>{saved.voucherNo}</strong> posted for{" "}
            {formatINR(saved.totalDebit)}.
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-success"
            onClick={() =>
              navigate(
                `${AccountsConstants.VOUCHER_VIEW}?id=${saved._id || saved.id}`,
              )
            }
          >
            <i className="bi bi-eye" /> View
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={clear}
          >
            New {config.label.toLowerCase()}
          </button>
          {voucherType === "PURCHASE" && settlementLink("PURCHASE", saved) && (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => navigate(settlementLink("PURCHASE", saved))}
            >
              Record payment
            </button>
          )}
        </div>
      )}
      <ApiAlert error={error} />
      {metaError && <div className="accounts-error">{metaError}</div>}
      {serverFields.preview && (
        <div className="accounts-error">{serverFields.preview}</div>
      )}
      <form onSubmit={save} noValidate>
        <div className="accounts-entry">
          {voucherType === "RECEIPT" && params.get("invoiceNr") && (
            <div className="flow-receipt-source">
              <i className="bi bi-cash-coin" aria-hidden="true" />
              <div>
                <strong>Receipt for {params.get("invoiceNr")}</strong>
                <span>
                  {[
                    params.get("partyName"),
                    params.get("orderNo")
                      ? `Order ${params.get("orderNo")}`
                      : "",
                    params.get("amount") ? formatINR(params.get("amount")) : "",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </div>
            </div>
          )}
          <CollapsibleSection title="Voucher">
            <div className="accounts-panel">
              <div className="accounts-entry-grid">
                <label className="tally-field">
                  Voucher No
                  <input
                    className="form-control"
                    readOnly
                    value={saved?.voucherNo || ""}
                    placeholder="Auto-generated on save"
                  />
                </label>
                <label className="tally-field">
                  Date *
                  <input
                    className={`form-control${show("voucherDate") ? " is-invalid" : ""}`}
                    type="date"
                    max={todayInput()}
                    value={form.voucherDate}
                    onChange={set("voucherDate")}
                    disabled={locked}
                  />
                  {show("voucherDate") && (
                    <span className="accounts-hint tally-error">
                      {show("voucherDate")}
                    </span>
                  )}
                </label>
                <label className="tally-field">
                  Reference No
                  <input
                    className="form-control"
                    maxLength={50}
                    value={form.referenceNo}
                    placeholder={
                      linkedFrom === "invoice"
                        ? "Invoice number"
                        : linkedFrom === "purchase"
                          ? "Purchase number"
                          : voucherType === "RECEIPT"
                            ? "Enter the invoice or bill number"
                            : voucherType === "PAYMENT"
                              ? "Enter the purchase number"
                              : "Optional reference"
                    }
                    onChange={set("referenceNo")}
                    disabled={locked}
                  />
                  {linkedFrom === "invoice" && (
                    <span className="accounts-hint">
                      Filled from the sales invoice. Change it if this receipt
                      covers a different bill.
                    </span>
                  )}
                  {linkedFrom === "purchase" && (
                    <span className="accounts-hint">
                      Filled from the purchase voucher. Change it if this
                      payment covers a different bill.
                    </span>
                  )}
                </label>
                {config.modeLabel && (
                  <label className="tally-field">
                    {config.modeLabel} *
                    <select
                      className={`form-select${show("paymentMode") ? " is-invalid" : ""}`}
                      value={form.paymentMode}
                      onChange={set("paymentMode")}
                      disabled={locked}
                    >
                      <option value="">Select</option>
                      {(meta?.paymentModes || [])
                        .filter((mode) =>
                          [
                            "BANK",
                            "CASH",
                            "CHEQUE",
                            "UPI",
                            "NEFT",
                            "RTGS",
                            "IMPS",
                          ].includes(mode.value),
                        )
                        .map((mode) => (
                          <option key={mode.value} value={mode.value}>
                            {mode.label}
                          </option>
                        ))}
                    </select>
                    {show("paymentMode") && (
                      <span className="accounts-hint tally-error">
                        {show("paymentMode")}
                      </span>
                    )}
                  </label>
                )}
                {isCheque && (
                  <>
                    <label className="tally-field">
                      Cheque Number *
                      <input
                        type="text"
                        className={`form-control${
                          show("chequeNumber") ? " is-invalid" : ""
                        }`}
                        value={form.chequeNumber}
                        onChange={set("chequeNumber")}
                        placeholder="Enter cheque number"
                        maxLength={30}
                        disabled={locked}
                      />
                      {show("chequeNumber") && (
                        <span className="accounts-hint tally-error">
                          {show("chequeNumber")}
                        </span>
                      )}
                    </label>

                    <label className="tally-field">
                      Cheque Date *
                      <input
                        type="date"
                        className={`form-control${
                          show("chequeDate") ? " is-invalid" : ""
                        }`}
                        value={form.chequeDate}
                        onChange={set("chequeDate")}
                        disabled={locked}
                      />
                      {show("chequeDate") && (
                        <span className="accounts-hint tally-error">
                          {show("chequeDate")}
                        </span>
                      )}
                    </label>
                  </>
                )}

                {isRTGS && (
                  <label className="tally-field">
                    RTGS Reference Number *
                    <input
                      type="text"
                      className={`form-control${
                        show("rtgsReferenceNumber") ? " is-invalid" : ""
                      }`}
                      value={form.rtgsReferenceNumber}
                      onChange={set("rtgsReferenceNumber")}
                      placeholder="Enter RTGS reference number"
                      maxLength={50}
                      disabled={locked}
                    />
                    {show("rtgsReferenceNumber") && (
                      <span className="accounts-hint tally-error">
                        {show("rtgsReferenceNumber")}
                      </span>
                    )}
                  </label>
                )}

                {isUPI && (
                  <label className="tally-field">
                    UPI Transaction Reference Number *
                    <input
                      type="text"
                      className={`form-control${
                        show("upiReferenceNumber") ? " is-invalid" : ""
                      }`}
                      value={form.upiReferenceNumber}
                      onChange={set("upiReferenceNumber")}
                      placeholder="Enter UPI transaction ID / UTR"
                      maxLength={100}
                      disabled={locked}
                    />
                    {show("upiReferenceNumber") && (
                      <span className="accounts-hint tally-error">
                        {show("upiReferenceNumber")}
                      </span>
                    )}
                  </label>
                )}

                {isNEFT && (
                  <label className="tally-field">
                    NEFT Reference Number *
                    <input
                      type="text"
                      className={`form-control${
                        show("neftReferenceNumber") ? " is-invalid" : ""
                      }`}
                      value={form.neftReferenceNumber}
                      onChange={set("neftReferenceNumber")}
                      placeholder="Enter NEFT reference number"
                      maxLength={50}
                      disabled={locked}
                    />
                    {show("neftReferenceNumber") && (
                      <span className="accounts-hint tally-error">
                        {show("neftReferenceNumber")}
                      </span>
                    )}
                  </label>
                )}
                {isIMPS && (
                  <label className="tally-field">
                    IMPS Reference Number *
                    <input
                      type="text"
                      className={`form-control${
                        show("impsReferenceNumber") ? " is-invalid" : ""
                      }`}
                      value={form.impsReferenceNumber}
                      onChange={set("impsReferenceNumber")}
                      placeholder="Enter IMPS reference number"
                      maxLength={50}
                      disabled={locked}
                    />
                    {show("impsReferenceNumber") && (
                      <span className="accounts-hint tally-error">
                        {show("impsReferenceNumber")}
                      </span>
                    )}
                  </label>
                )}

                {(isIMPS || isRTGS || isUPI || isNEFT || isCheque) && (
                  <label className="tally-field">
                    Customer Bank Name
                    <input
                      className="form-control"
                      maxLength={50}
                      value={form.customerBankName}
                      onChange={set("customerBankName")}
                      disabled={locked}
                    />
                  </label>
                )}
                <LedgerCombo
                  label={config.debit.label}
                  required
                  disabled={locked}
                  groups={meta?.ledgerGroups || []}
                  ledgers={debitLedgers}
                  value={form.debitLedgerId}
                  onChange={(id) =>
                    setForm((current) => ({ ...current, debitLedgerId: id }))
                  }
                  error={show("debitLedgerId")}
                />
                <LedgerCombo
                  label={config.credit.label}
                  required
                  disabled={locked}
                  groups={meta?.ledgerGroups || []}
                  ledgers={creditLedgers}
                  value={form.creditLedgerId}
                  onChange={(id) =>
                    setForm((current) => ({ ...current, creditLedgerId: id }))
                  }
                  error={show("creditLedgerId")}
                />
                <label className="tally-field">
                  Amount (₹) *
                  <input
                    className={`form-control${show("amount") ? " is-invalid" : ""}`}
                    value={form.amount}
                    placeholder="0.00"
                    onChange={set("amount")}
                    disabled={locked}
                    inputMode="decimal"
                  />
                  <span className="accounts-hint">
                    {show("amount") ||
                      (amountOk(form.amount) ? formatINR(form.amount) : " ")}
                  </span>
                </label>
                <label className="tally-field accounts-span">
                  Narration
                  <textarea
                    className="form-control"
                    rows={2}
                    maxLength={500}
                    value={form.narration}
                    placeholder="What this voucher is for"
                    onChange={set("narration")}
                    disabled={locked}
                  />
                  <span className="accounts-hint">
                    {form.narration.length}/500
                  </span>
                </label>
              </div>
            </div>
          </CollapsibleSection>
          {voucherType === "RECEIPT" && (
            <CollapsibleSection
              title="Invoice allocation"
              subtitle="One receipt can clear several invoices. The rest stays as advance."
            >
              <ReceiptSplit
                ledgerId={form.creditLedgerId}
                receiptAmount={form.amount}
                allocations={allocations}
                onChange={setAllocations}
                onIssue={setSplitIssue}
                onAdvance={setAdvanceAvailable}
                disabled={locked}
                presetInvoice={params.get("invoiceNr") || ""}
              />
              {clientErrors.allocations && (
                <div className="accounts-error">{clientErrors.allocations}</div>
              )}
            </CollapsibleSection>
          )}
          <CollapsibleSection title="Accounting entry preview">
            <div className="accounts-panel">
              <EntryPreview lines={lines} />
            </div>
          </CollapsibleSection>
        </div>
        {!locked && (
          <div className="accounts-form-actions">
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={clear}
              disabled={saving}
            >
              Clear
            </button>
            <button className="btn tally-btn" type="submit" disabled={saving}>
              {saving
                ? "Saving…"
                : voucherType === "RECEIPT"
                  ? "Send receipt for approval"
                  : `Save ${config.label.toLowerCase()}`}
            </button>
          </div>
        )}
      </form>
    </PageShell>
  );
}

export { default as EntryPreview } from "../../Components/Accounts/EntryPreview";
